// Sold vs actual role split, for the planner's detail panel (RoleSplitCard).
import type { ResourceRole, ResourceSplit } from './resourcingApi';

export interface SplitRow {
  id: string;
  name: string;
  /** Share in the sold split, or null when the quote has no hours yet. */
  sold: number | null;
  /** Share in the actual split, which is the sold one until someone changes it; null when neither exists. */
  actual: number | null;
  plannedHours: number;
  plannedPct: number;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** One row per role in the sold split, the actual split or the plan, in role order. */
export function splitRows(split: ResourceSplit, roles: ResourceRole[]): SplitRow[] {
  const sold = new Map(split.sold.map((s) => [s.resource_role_id, s.share_pct]));
  const actual = split.actual ? new Map(split.actual.map((s) => [s.resource_role_id, s.share_pct])) : sold;
  const held = new Map(split.held.map((h) => [h.resource_role_id, h.hours]));
  const heldTotal = split.held.reduce((sum, h) => sum + h.hours, 0);
  const used = new Set([...sold.keys(), ...actual.keys(), ...held.keys()]);
  const known = roles.filter((r) => used.has(r.id) || r.active);
  // Keep rows for any role the split mentions, even one no longer listed.
  for (const id of used) if (!known.some((r) => r.id === id)) known.push({ id, name: 'Unknown role', sort_order: 999, active: false });
  return known
    .filter((r) => used.has(r.id) || split.sold.length > 0)
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
    .map((r) => {
      const hours = held.get(r.id) ?? 0;
      return {
        id: r.id,
        name: r.name,
        sold: split.sold.length ? (sold.get(r.id) ?? 0) : null,
        actual: split.actual || split.sold.length ? (actual.get(r.id) ?? 0) : null,
        plannedHours: round1(hours),
        plannedPct: heldTotal > 0 ? Math.round((100 * hours) / heldTotal) : 0,
      };
    });
}

/** The draft's shares added up, as typed (blank is 0). */
export function splitTotal(draft: Record<string, string>): number {
  return round1(Object.values(draft).reduce((sum, v) => sum + (Number(v) || 0), 0));
}
